import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useRevalidator } from 'react-router-dom';
import type { ZodIssue } from 'zod';
import { Button, Input, Label, Modal, cn } from '@immo/ui-kit';
import {
  IssueAdminApiKeyBodySchema,
  type AdminApiKeyOwner,
  type AdminWorkspaceListItem,
  type IssueAdminApiKeyResponse,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { CopyIcon, SearchIcon } from '@/components/layout/icons';
import {
  groupAdminGrantablePermissions,
  useModuleLabel,
  usePermissionLabel,
  usePermissionLiteral,
} from '@/lib/permissions/catalogue';
import { PermissionCatalogue } from '@/components/permission-catalogue';
import { ALL_WORKSPACES_QUERY } from '@/lib/test-workspaces/include-test-workspaces';
import { FormField } from '@/components/form';

type Field = 'workspaceId' | 'name' | 'description' | 'rateLimit' | 'permissions';

type FormErrors = Readonly<Record<Field, string | null>>;

const NO_ERRORS: FormErrors = {
  workspaceId: null,
  name: null,
  description: null,
  rateLimit: null,
  permissions: null,
};

const PERMISSION_GROUPS = groupAdminGrantablePermissions();

/**
 * Where each exit lands (IMM-622).
 *
 * This dialog is a **route** — `/workspaces/api-keys/new`, a child of
 * `/workspaces/api-keys` — so closing it is a navigation, not a local `open`
 * flag, and "cancel does not navigate" would leave it permanently open.
 *
 * The two destinations coincide *here*, because this dialog's parent already
 * IS the key list: cancelling has always returned the operator to the page they
 * opened it from. They are still written apart, because the sibling
 * `new-admin-key.tsx` carries the identical handler over a different parent
 * (`/admins`) where the collapsed form was a real defect — and a shape that is
 * only accidentally correct is the one that gets copied next.
 */
const CANCELLED_DESTINATION = '/workspaces/api-keys';
const ISSUED_DESTINATION = '/workspaces/api-keys';

function fieldFromPath(path: ReadonlyArray<PropertyKey>): Field | undefined {
  const head = path[0];

  if (head === 'workspaceId') return 'workspaceId';

  if (head === 'name') return 'name';

  if (head === 'description') return 'description';

  if (head === 'rateLimit') return 'rateLimit';

  if (head === 'grantedPermissions') return 'permissions';

  return undefined;
}

function errorMessageKey(field: Field, issue: ZodIssue): string {
  if (field === 'workspaceId') return 'workspace-required';

  if (field === 'name') return issue.code === 'too_big' ? 'name-too-long' : 'name-required';

  if (field === 'description') return 'description-too-long';

  if (field === 'rateLimit') return 'rate-limit-range';

  return 'permissions-required';
}

export function IssueApiKeyPage() {
  const t = useTranslations('pages.admin.api-keys.issue');
  const permissionLabel = usePermissionLabel();
  const permissionIdentifier = usePermissionLiteral();
  const moduleLabel = useModuleLabel();
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();

  const [workspaces, setWorkspaces] = useState<ReadonlyArray<AdminWorkspaceListItem>>([]);
  const [workspaceQuery, setWorkspaceQuery] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [owner, setOwner] = useState<AdminApiKeyOwner | null>(null);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rateLimit, setRateLimit] = useState('50');
  const [permissions, setPermissions] = useState<ReadonlyArray<string>>([]);
  const [permissionQuery, setPermissionQuery] = useState('');

  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState<IssueAdminApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Monotonic sequence guarding `resolveOwner` against out-of-order responses:
  // only the response for the latest workspace-select wins, so a slow resolve
  // for a previously-picked workspace can never overwrite the current owner.
  const resolveSeq = useRef(0);

  useEffect(() => {
    let active = true;

    // Every workspace, test tenants included: issuing a key is a write, and
    // writes are never gated by the operator's test-workspace filter.
    void apiClient.adminWorkspaces.list({ query: ALL_WORKSPACES_QUERY }).then((result) => {
      if (!active) return;

      if (result.ok) setWorkspaces(result.body.data);
    });

    return () => {
      active = false;
    };
  }, []);

  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === selectedWorkspaceId) ?? null,
    [workspaces, selectedWorkspaceId],
  );

  const filteredWorkspaces = useMemo(() => {
    const query = workspaceQuery.trim().toLowerCase();

    if (query === '') return workspaces;

    return workspaces.filter((workspace) => workspace.name.toLowerCase().includes(query));
  }, [workspaces, workspaceQuery]);

  const clearError = (key: Field) => setErrors((prev) => (prev[key] === null ? prev : { ...prev, [key]: null }));

  const selectWorkspace = (workspace: AdminWorkspaceListItem) => {
    setSelectedWorkspaceId(workspace.id);
    setOwner(null);
    setOwnerError(null);
    setResolving(true);
    clearError('workspaceId');

    const seq = resolveSeq.current + 1;
    resolveSeq.current = seq;

    void apiClient.adminApiKeys
      .resolveOwner({ pathParams: { workspaceId: workspace.id } })
      .then((result) => {
        if (seq !== resolveSeq.current) return;

        if (result.ok && result.body.owner !== null) {
          setOwner(result.body.owner);
        } else if (result.ok) {
          setOwnerError(t('errors.no-eligible-owner', { workspace: workspace.name }));
        } else {
          setOwnerError(t('errors.generic'));
        }
      })
      .catch(() => {
        if (seq === resolveSeq.current) setOwnerError(t('errors.generic'));
      })
      .finally(() => {
        if (seq === resolveSeq.current) setResolving(false);
      });
  };

  const togglePermission = (permission: string) => {
    clearError('permissions');
    setPermissions((prev) =>
      prev.includes(permission) ? prev.filter((entry) => entry !== permission) : [...prev, permission],
    );
  };

  const setGroupSelection = (groupPermissions: ReadonlyArray<string>, selected: boolean) => {
    clearError('permissions');
    setPermissions((prev) => {
      if (selected) {
        const next = new Set(prev);

        for (const permission of groupPermissions) next.add(permission);

        return [...next];
      }

      return prev.filter((permission) => !groupPermissions.includes(permission));
    });
  };

  const close = () => {
    if (submitting) return;

    if (issued !== null) {
      revalidator.revalidate();
      navigate(ISSUED_DESTINATION);

      return;
    }

    // Cancel: nothing was created, so the list is not revalidated.
    navigate({ pathname: CANCELLED_DESTINATION, search: location.search });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericError(null);

    const trimmedRate = rateLimit.trim();
    const parsed = IssueAdminApiKeyBodySchema.safeParse({
      workspaceId: selectedWorkspaceId ?? '',
      name,
      ...(description.trim().length > 0 ? { description } : {}),
      ...(trimmedRate.length > 0 ? { rateLimit: Number(trimmedRate) } : {}),
      grantedPermissions: permissions,
    });

    if (!parsed.success) {
      const next: Record<Field, string | null> = { ...NO_ERRORS };

      for (const issue of parsed.error.issues) {
        const field = fieldFromPath(issue.path);

        if (field !== undefined && next[field] === null) {
          next[field] = t(`errors.${errorMessageKey(field, issue)}`);
        }
      }

      setErrors(next);

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminApiKeys.issue({ body: parsed.data });

      if (result.ok) {
        setIssued(result.body);

        return;
      }

      const body = result.body as { error?: { code?: string; message?: string } } | undefined;
      const code = body?.error?.code;

      // The refusal names no permission (IMM-569.3): the wire message is part of
      // the API response, so it would echo the bound member's own authorisation
      // state back over it. The offending literal is in the backend log.
      if (code === 'identity.api-key.permission-not-held') {
        setGenericError(t('errors.permission-not-held', { member: owner?.name ?? owner?.email ?? '' }));
      } else if (code === 'identity.api-key.no-eligible-owner') {
        setGenericError(t('errors.no-eligible-owner', { workspace: selectedWorkspace?.name ?? '' }));
      } else if (result.status === 403) {
        setGenericError(t('errors.forbidden'));
      } else {
        setGenericError(t('errors.generic'));
      }
    } catch {
      setGenericError(t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = async () => {
    if (issued === null) return;

    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
    } catch {
      // Clipboard access can be denied; the secret stays visible for manual copy.
    }
  };

  if (issued !== null) {
    return (
      <Modal
        open
        onClose={close}
        size="md"
        title={t('secret-heading')}
        footer={
          <Button type="button" onClick={close}>
            {t('done-cta')}
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-base-sm text-text-secondary">{t('secret-warning')}</p>
          <div className="flex items-center gap-2">
            <Input readOnly value={issued.secret} className="font-mono" aria-label={t('secret-heading')} />
            <Button type="button" variant="ghost" onClick={copySecret}>
              <CopyIcon className="h-4 w-4" />
              {copied ? t('copied-cta') : t('copy-cta')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const submitDisabled = submitting || selectedWorkspaceId === null || resolving || owner === null;
  const permissionsDisabled = submitting || selectedWorkspaceId === null;

  return (
    <Modal
      open
      onClose={close}
      dirty={selectedWorkspaceId !== null || name !== '' || description !== '' || permissions.length > 0}
      size="lg"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('cancel-cta')}
          </Button>
          <Button type="submit" form="issue-admin-api-key-form" disabled={submitDisabled}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="issue-admin-api-key-form" className="space-y-5" onSubmit={submit} noValidate>
        <div className="space-y-1">
          <Label htmlFor="issue-workspace-search">{t('workspace-label')}</Label>
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <Input
              id="issue-workspace-search"
              className="pl-8"
              value={workspaceQuery}
              placeholder={t('workspace-search-placeholder')}
              onChange={(e) => setWorkspaceQuery(e.target.value)}
              disabled={submitting}
              invalid={errors.workspaceId !== null}
            />
          </div>
          {filteredWorkspaces.length === 0 ? (
            <p className="px-1 py-2 text-sm text-text-tertiary">{t('workspace-empty')}</p>
          ) : (
            <ul className="max-h-40 overflow-y-auto rounded-md border border-border" aria-label={t('workspace-label')}>
              {filteredWorkspaces.map((workspace) => (
                <li key={workspace.id}>
                  <button
                    type="button"
                    onClick={() => selectWorkspace(workspace)}
                    aria-pressed={workspace.id === selectedWorkspaceId}
                    disabled={submitting}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2 text-left text-base-sm text-text-primary focus-visible:shadow-focus focus-visible:outline-none',
                      workspace.id === selectedWorkspaceId ? 'bg-interactive-subtle-bg' : 'hover:bg-subtle',
                    )}
                  >
                    <span>{workspace.name}</span>
                    <span className="text-text-tertiary">{workspace.owner.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/* Announced but still unassociated: the workspace picker is a button list with
              no `aria-describedby` seam, so the refusal is spoken when it appears but cannot
              be reached from the control. IMM-849 gives the widget that seam (BL-115). */}
          {errors.workspaceId !== null && (
            <p role="alert" className="text-sm text-danger-600">
              {errors.workspaceId}
            </p>
          )}
          {owner !== null && (
            <div className="space-y-0.5 pt-1">
              <p className="text-base-sm font-medium text-text-primary">{t('owner-line', { email: owner.email })}</p>
              <p className="text-sm text-text-tertiary">{t('owner-helper')}</p>
            </div>
          )}
          {/* Announced but still unassociated — same seam gap as the workspace error above,
              on the owner lookup. IMM-849 (BL-115). */}
          {ownerError !== null && (
            <p role="alert" className="pt-1 text-sm text-danger-600">
              {ownerError}
            </p>
          )}
        </div>

        <FormField id="issue-name" label={t('name-label')} error={errors.name}>
          {(errId) => (
            <Input
              id="issue-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearError('name');
              }}
              disabled={submitting}
              invalid={errors.name !== null}
              aria-describedby={errId}
            />
          )}
        </FormField>

        <FormField id="issue-description" label={t('description-label')} error={errors.description}>
          {(errId) => (
            <Input
              id="issue-description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                clearError('description');
              }}
              disabled={submitting}
              invalid={errors.description !== null}
              aria-describedby={errId}
            />
          )}
        </FormField>

        <div className="space-y-1">
          <Label htmlFor="issue-rate-limit">{t('rate-limit-label')}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="issue-rate-limit"
              type="number"
              inputMode="numeric"
              min={1}
              max={60}
              className="w-24"
              value={rateLimit}
              onChange={(e) => {
                setRateLimit(e.target.value);
                clearError('rateLimit');
              }}
              disabled={submitting}
              invalid={errors.rateLimit !== null}
              aria-describedby={errors.rateLimit !== null ? 'issue-rate-limit-error' : undefined}
            />
            <span className="text-base-sm text-text-tertiary">{t('rate-limit-suffix')}</span>
          </div>
          <p className="text-sm text-text-tertiary">{t('rate-limit-helper')}</p>
          {errors.rateLimit !== null && (
            <p id="issue-rate-limit-error" role="alert" className="text-sm text-danger-600">
              {errors.rateLimit}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('permissions-label')}</Label>
          <p className="text-sm text-text-tertiary">{t('permissions-helper')}</p>
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <Input
              className="pl-8"
              value={permissionQuery}
              placeholder={t('permission-search-placeholder')}
              onChange={(e) => setPermissionQuery(e.target.value)}
              disabled={permissionsDisabled}
              aria-label={t('permission-search-placeholder')}
            />
          </div>
          <PermissionCatalogue
            groups={PERMISSION_GROUPS}
            query={permissionQuery}
            selected={permissions}
            disabled={permissionsDisabled}
            label={permissionLabel}
            literal={permissionIdentifier}
            moduleLabel={moduleLabel}
            scopeLabel={(scope) => t(`scope.${scope}`)}
            selectAllLabel={t('select-all')}
            deselectAllLabel={t('deselect-all')}
            emptyLabel={t('permission-empty')}
            onToggle={togglePermission}
            onSetGroup={setGroupSelection}
          />
          {/* Announced but still unassociated — `PermissionCatalogue` takes no
              `aria-describedby` prop. IMM-849 gives it one (BL-115). */}
          {errors.permissions !== null && (
            <p role="alert" className="text-sm text-danger-600">
              {errors.permissions}
            </p>
          )}
        </div>

        {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
      </form>
    </Modal>
  );
}
